import { ADMIN_GROUP_ALIAS, isAdminRole, type SessionUser } from '@hm/contracts'
import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  BarChart3,
  Boxes,
  Building2,
  ClipboardList,
  History,
  Inbox,
  LifeBuoy,
  MessagesSquare,
  Package,
  PackageCheck,
  Receipt,
  Search,
  Settings,
  ShoppingCart,
  Store,
  Tags,
  Truck,
  Users,
  Wallet,
} from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { HexNut } from '~/components/brand'
import { PageHeader, SectionLabel } from '~/components/page'
import { Dot } from '~/components/ui/primitives'
import type { MessageKey } from '~/i18n'
// Counterparty tiles are gated on the kind of account, not on a permission — the
// owner holds every permission, and "Your basket" is not a thing the owner has.
import { can, isBuyer, isSeller } from '~/lib/access'
import { useLabels, useT } from '~/lib/i18n'
import { useCan, useSession } from '~/lib/session'
import { anchor } from '~/lib/tour/anchors'

/**
 * Role-specific home: a handful of large actions rather than a wall of numbers.
 *
 * For a non-technical audience the landing screen's job is to answer "what do I do
 * now", not "how is the business performing" — the numbers live on their own screen
 * for the person whose job that is. Screens that exist for occasional reference sit
 * in the quiet row underneath, because equal weight would imply equal urgency.
 */
export function HomePage() {
  const t = useT()
  const user = useSession()
  const can = useCan()
  const firstName = user.name.split(/\s+/)[0] ?? user.name
  const admin = isAdminRole(user.role)

  return (
    <>
      <PageHeader
        eyebrow={t(greeting())}
        title={firstName}
        description={
          admin
            ? t('home.turn_what_customers_are_asking_for')
            : can('catalog:browse')
              ? t('home.everything_you_need_at_a_price')
              : t('home.keep_your_prices_current_and_answer')
        }
      />

      <Banner />

      <SectionLabel className="mt-8">{t('home.what_you_can_do')}</SectionLabel>
      <div {...anchor('home.tiles')} className="grid gap-3 sm:grid-cols-2">
        {TILES.filter((tile) => visible(tile, user)).map((tile) => (
          <ActionCard key={tile.to} {...tile} />
        ))}
      </div>

      <div {...anchor('home.quiet')} className="mt-4 flex flex-wrap gap-2">
        {QUIET.filter((link) => visible(link, user)).map((link) => (
          <QuietLink key={link.to} to={link.to} icon={link.icon}>
            {t(link.label)}
          </QuietLink>
        ))}
      </div>
    </>
  )
}

interface Tile {
  to: string
  icon: ComponentType<{ className?: string }>
  title: MessageKey
  description: MessageKey
  show: (user: SessionUser) => boolean
  /** Kept off the landing screen until Advanced mode is on. See components/advanced.tsx. */
  advanced?: true
}

const visible = (item: { show: (u: SessionUser) => boolean; advanced?: true }, user: SessionUser) =>
  item.show(user) && (user.advancedMode || !item.advanced)

/**
 * Ordered by how often each is the answer to "what now", not by hierarchy. A buyer
 * shops; a shop keeps its prices current; the desk answers requests.
 */
const TILES: Tile[] = [
  {
    to: '/requests',
    icon: ClipboardList,
    title: 'common.price_requests',
    description: 'home.tile.requests_desc',
    show: can('rfq:manage'),
  },
  {
    to: '/messages',
    icon: MessagesSquare,
    title: 'common.messages',
    description: 'home.tile.messages_desc',
    show: can('chat:participate'),
  },
  {
    to: '/shop',
    icon: Store,
    title: 'common.shop',
    description: 'home.tile.shop_desc',
    show: isBuyer,
  },
  {
    to: '/cart',
    icon: ShoppingCart,
    title: 'common.your_basket',
    description: 'home.tile.cart_desc',
    show: isBuyer,
  },
  {
    to: '/items',
    icon: Package,
    title: 'common.my_items',
    description: 'home.tile.items_desc',
    show: isSeller,
  },
  {
    to: '/price-checks',
    icon: ClipboardList,
    title: 'common.price_checks',
    description: 'home.tile.price_checks_desc',
    show: isSeller,
  },
  {
    to: '/supply-orders',
    icon: PackageCheck,
    title: 'common.orders_to_fill',
    description: 'home.tile.supply_orders_desc',
    show: isSeller,
  },
  {
    to: '/orders',
    icon: Receipt,
    title: 'common.orders',
    description: 'home.tile.orders_desc',
    show: (u) => can('order:manage')(u) || can('order:read_own')(u),
  },
  {
    to: '/pricing',
    icon: Tags,
    title: 'common.customer_prices',
    description: 'home.tile.pricing_desc',
    show: can('admin_listing:manage'),
  },
  {
    to: '/sourcing',
    icon: Search,
    title: 'common.shop_prices',
    description: 'home.tile.sourcing_desc',
    show: can('sourcing:manage'),
  },
  {
    to: '/stock',
    icon: Boxes,
    title: 'common.our_stock',
    description: 'home.tile.stock_desc',
    show: can('platform_stock:manage'),
  },
  {
    to: '/money',
    icon: Wallet,
    title: 'common.money',
    description: 'home.tile.money_desc',
    show: can('ledger:read'),
  },
]

const QUIET: {
  to: string
  icon: ComponentType<{ className?: string }>
  label: MessageKey
  show: (user: SessionUser) => boolean
  advanced?: true
}[] = [
  { to: '/catalog', icon: Tags, label: 'common.item_list', show: can('product:create') },
  {
    to: '/catalog/suggestions',
    icon: Inbox,
    label: 'common.suggested_items',
    show: can('product_proposal:review'),
  },
  {
    to: '/purchase-orders',
    icon: Truck,
    label: 'common.shop_orders',
    show: can('purchase_order:manage'),
  },
  { to: '/businesses', icon: Building2, label: 'common.businesses', show: can('org:read') },
  { to: '/people', icon: Users, label: 'common.people', show: can('user:read') },
  { to: '/reports', icon: BarChart3, label: 'common.numbers', show: can('report:read') },
  { to: '/activity', icon: History, label: 'home.quiet.activity', show: can('audit:read') },
  { to: '/settings', icon: Settings, label: 'common.settings', show: () => true },
  { to: '/guide', icon: LifeBuoy, label: 'tour.show_me_around', show: () => true },
]

function greeting(): MessageKey {
  const hour = new Date().getHours()
  if (hour < 12) return 'home.good_morning'
  if (hour < 17) return 'home.good_afternoon'
  return 'home.good_evening'
}

/** The one place the platform states what it is and that it is live. */
function Banner() {
  const L = useLabels()
  const t = useT()
  const user = useSession()
  const admin = isAdminRole(user.role)
  const seller = user.orgType === 'seller'

  return (
    <div
      // Always dark, in either theme — `bg-surface` (not canvas) so it still lifts
      // off the page when the rest of the app is dark too.
      data-theme="dark"
      {...anchor('home.banner')}
      className="glow relative isolate overflow-hidden rounded-card border border-border bg-surface px-5 py-6 text-ink shadow-lift sm:px-7 sm:py-7"
    >
      {/* No `.blueprint` here: the grid is anchored to its own box origin, so a
          second one inside a panel lands out of phase with the page's. */}
      <HexNut className="pointer-events-none absolute -top-8 -right-10 -z-10 size-56 opacity-[0.09]" />

      <div className="flex flex-wrap items-center gap-2.5">
        <span className="inline-flex items-center gap-1.5 rounded-chip bg-surface-muted px-2 py-1">
          <Dot tone="success" />
          <span className="stencil text-ink-muted">{t('home.connected')}</span>
        </span>
        <span className="stencil rounded-chip bg-primary-soft px-2 py-1 text-primary">
          {admin ? ADMIN_GROUP_ALIAS : (user.orgName ?? L.role[user.role])}
        </span>
      </div>

      <p className="mt-4 max-w-md text-[1.35rem] leading-snug font-extrabold text-balance">
        {admin
          ? t('home.you_sit_between_the_shop_and')
          : seller
            ? t('home.orders_without_chasing_customers')
            : t('home.one_desk_for_every_price_quote')}
      </p>
      <p className="mt-2 max-w-md text-sm text-ink-muted">
        {admin
          ? t('home.buyers_and_sellers_only_ever_talk')
          : seller
            ? t('home.keep_your_rates_current_and_answer', { admingroupalias: ADMIN_GROUP_ALIAS })
            : t('home.talk_to_admingroupalias_for_anything_prices', {
                admingroupalias: ADMIN_GROUP_ALIAS,
              })}
      </p>

      <div className="hazard mt-6 h-1.5 w-32 rounded-full opacity-80" aria-hidden />
    </div>
  )
}

function ActionCard({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string
  icon: ComponentType<{ className?: string }>
  title: MessageKey
  description: MessageKey
}) {
  const t = useT()
  return (
    <Link
      to={to}
      className="group relative overflow-hidden rounded-card border border-border bg-surface p-4 shadow-card transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-primary-line hover:shadow-lift"
    >
      <div className="flex items-start gap-3.5">
        <span className="hex grid size-11 shrink-0 place-items-center bg-primary-soft text-primary transition-transform duration-200 group-hover:scale-105">
          <Icon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-ink">{t(title)}</p>
          <p className="mt-0.5 text-sm text-ink-muted">{t(description)}</p>
        </div>
        <ArrowRight
          className="mt-1 size-4 shrink-0 text-ink-subtle transition-transform duration-200 group-hover:translate-x-1 group-hover:text-primary"
          aria-hidden
        />
      </div>
    </Link>
  )
}

/** Secondary destination: reachable, but not competing with the day's work. */
function QuietLink({
  to,
  icon: Icon,
  children,
}: {
  to: string
  icon: ComponentType<{ className?: string }>
  children: ReactNode
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 rounded-control border border-border bg-surface px-3 py-2 text-sm font-semibold text-ink-muted transition-colors hover:border-border-strong hover:text-ink"
    >
      <Icon className="size-4 text-ink-subtle" aria-hidden />
      {children}
    </Link>
  )
}
